import { IconComponent, IconProps } from 'app/src/kit/Icon'
import Icons from 'app/src/kit/Icons'
import { interactiveBackgroundHover } from 'app/src/kit/mixins/interactive'
import Text from 'app/src/kit/Text'
import { WeenatPressableProps } from 'app/src/kit/WeenatPressable'
import logEvent from 'app/utils/analytics'
import isNil from 'lodash-es/isNil'
import { MouseEventHandler, ReactNode } from 'react'
import { css, styled } from 'styled-components'

type Event = 'affiliate' | 'alert' | 'device' | 'integration' | 'member' | 'plot' | 'vws'

const Container = styled(Flex)<{ $isDisabled: boolean }>`
  flex-direction: row;
  align-items: center;

  border-radius: ${(p) => p.theme.radiuses.md}px;
  padding: ${(p) => p.theme.spacings.md}px;
  gap: ${(p) => p.theme.spacings.md}px;

  min-height: 48px;

  cursor: pointer;

  ${(p) =>
    p.$isDisabled &&
    css`
      background-color: ${p.theme.colors.alpha.black[5]};

      p,
      svg {
        opacity: 0.6;
      }
    `}

  ${interactiveBackgroundHover}
`

type AddModalItemProps = Pick<WeenatPressableProps, 'onPress' | 'isDisabled'> & {
  buttonSlot?: ReactNode
  description?: ReactNode
  /** Name of the event to log on click, this will be prefixed with ""add_modal_select_" */
  event?: `add_md_${Event}`
  Icon: IconComponent
  label: ReactNode
}

const AddModalItem: FC<AddModalItemProps> = ({
  buttonSlot,
  description,
  event,
  Icon,
  isDisabled = false,
  label,
  onPress
}) => {
  const color = isDisabled ? 'grayscale.700' : undefined

  const defaultIconProps: Partial<IconProps> = {
    $size: 'lg',
    $color: color
  }

  const handlePress: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isNil(event)) {
      logEvent(event)
    }
    if (!isDisabled) {
      onPress?.(e)
    }
  }

  const TrailingIcon = isDisabled ? Icons.Lock : Icon

  return (
    <Container $isDisabled={isDisabled} onClick={handlePress}>
      <TrailingIcon {...defaultIconProps} $color={color} />
      <Flex $flexDirection='column' $flex={1} $gap={'sm'}>
        {typeof label === 'string' ? (
          <Text $color={color} $fontWeight='medium' $lineHeight='none'>
            {label}
          </Text>
        ) : (
          label
        )}
        {!isNil(description) ? (
          <>
            {typeof description === 'string' ? (
              <Text $color={color} $fontSize='sm' $lineHeight='sm'>
                {description}
              </Text>
            ) : (
              description
            )}
          </>
        ) : null}
      </Flex>
      {buttonSlot}
    </Container>
  )
}

export default AddModalItem
